import React, { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import Terms from "../pages/Terms";

const TermsModal = ({ open, onClose }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 modal-backdrop">
      <div className="modal-content w-full max-w-lg p-6 rounded shadow-lg relative">
        <div className="text-sm space-y-2 max-h-[60vh] overflow-y-auto">
          <Terms />
        </div>
        <div className="modal-actions mt-6 flex justify-end gap-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/80 transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const RegisterForm = ({ onClose, onSwitch }) => {
  const [form, setForm] = useState({ username: "", email: "", password: "", confirmPassword: "" });
  const [acceptedTerms, setAcceptedTerms] = useState(false);   
  const [showPassword, setShowPassword] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);  
  const { executeRecaptcha } = useGoogleReCaptcha();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const email = params.get("email");
    if (email) setForm((f) => ({ ...f, email }));
  }, [location]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const register = async () => {
    if (!acceptedTerms) {
      alert("You must accept the Terms and Conditions.");
      return;
    }	  
	  
    if (form.password !== form.confirmPassword) {
      alert("Passwords do not match");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      alert("Invalid email format");
      return;
    }

    const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
    if (!strongPasswordRegex.test(form.password)) {
      alert("Weak password");
      return;
    }

    if (!executeRecaptcha) {
      alert("Recaptcha not available");
      return;
    }

    const token = await executeRecaptcha("register");

    const response = await fetch(`${process.env.REACT_APP_API_URL}/users/register/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...form, recaptcha_token: token }),
    });

    const data = await response.json();

    if (response.ok) {
      alert("Registration successful! Please check your email.");
      onClose();
    } else {
      alert(data.detail || "Registration failed");
    }
  };

  return (
    <div className="container max-w-md w-full bg-white rounded-xl shadow p-6 sm:p-8 mx-auto mt-8">
      <h2 className="text-2xl font-bold text-center text-primary mb-6">Register for TripsFlare</h2>
      <div className="space-y-4">
        <input
          type="text"
          name="username"
          placeholder="Username"
          value={form.username}
          onChange={handleChange}
          className="form-input"
        />
        <input
          type="email"
          name="email"
          placeholder="Email"
          value={form.email}
          onChange={handleChange}
          className="form-input"
        />
		<div className="relative">
        <input
          type={showPassword ? "text" : "password"}
          name="password"
          placeholder="Password"
          value={form.password}
          onChange={handleChange}
          className="form-input pr-10"
        />
          <div
            className="absolute inset-y-0 right-3 flex items-center cursor-pointer"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <FaEyeSlash /> : <FaEye />}
          </div>
        </div>
        <div className="relative">		
        <input
          type={showPassword ? "text" : "password"}
          name="confirmPassword"
          placeholder="Confirm Password"
          value={form.confirmPassword}
          onChange={handleChange}
          className="form-input pr-10"
        />
          <div
            className="absolute inset-y-0 right-3 flex items-center cursor-pointer"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <FaEyeSlash /> : <FaEye />}
          </div>
        </div>		
        <label className="flex items-center text-sm">
          <input
            type="checkbox"
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
            className="mr-2"
          />
          I agree to the{" "}
          <button
            type="button"
            onClick={() => setTermsOpen(true)}
            className="text-primary underline ml-1"
          >
            Terms and Conditions
          </button>
        </label>		
        <button
          onClick={register}
          className="btn-full bg-secondary text-white p-3 rounded hover:bg-green-700 transition"
        >
          Register
        </button>
        <p className="text-center text-sm mt-4">
          Already have an account?{" "}
          <button onClick={onSwitch} className="text-primary underline">
            Login
          </button>
        </p>
      </div>
      {/* Terms Modal */}
      <TermsModal open={termsOpen} onClose={() => setTermsOpen(false)} />	  
    </div>
  );
};

const Register = (props) => (
  <GoogleReCaptchaProvider reCaptchaKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}>
    <RegisterForm {...props} />
  </GoogleReCaptchaProvider>
);

export default Register;
